import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { useApiQuery } from '@/hooks/useApiQuery';
import { hotelService } from '@/services/hotel.service';
import { useLivePageContent } from '@/hooks/usePageContent';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { ActiveSearchChips, normText } from '@/components/public/ActiveSearchChips';
import { FavoriteButton } from '@/components/public/FavoriteButton';
import { CetsTrees } from '@/components/CetsTrees';
import { ExperiencesCrossSell } from '@/components/public/ExperiencesCrossSell';
import { cetsSelfService } from '@/services/cets.service';
import { Seo } from '@/components/Seo';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, BedDouble, ArrowRight, Building2, ImageIcon, Calendar, Users, Hotel, Home, Coffee } from 'lucide-react';

const HERO = 'https://images.pexels.com/photos/17983542/pexels-photo-17983542.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920';
const SLUG = 'alloggi';
// Macro-categorie ospitalità (portale "Vacanze").
const CATS = [
  { key: 'hotel', label: 'Hotel', icon: Hotel },
  { key: 'casa_vacanze', label: 'Case vacanze', icon: Home },
  { key: 'bnb', label: 'B&B', icon: Coffee },
];
const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');
const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }); } catch { return iso; } };

function StructureCard({ s, rating }) {
  return (
    <Link to={`/struttura/${s.owner_id}`} className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`alloggi-card-${s.owner_id}`}>
      <div className="relative h-52 overflow-hidden bg-muted">
        <FavoriteButton structureId={s.owner_id} structureType="hotel" className="absolute left-3 top-3 z-10" />
        {rating && <div className="absolute bottom-3 left-3 z-10"><CetsTrees rating={rating} ownerId={s.owner_id} size="sm" showLabel={false} /></div>}
        {s.cover_image ? (
          <img src={mediaUrl(s.cover_image)} alt={s.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-9 w-9 text-muted-foreground" /></div>
        )}
        {s.from_price != null && (
          <span className="absolute right-3 top-3 rounded-full bg-background/90 px-3 py-1 text-xs font-semibold text-foreground backdrop-blur">da {euro(s.from_price)} / notte</span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display text-xl font-semibold text-foreground">{s.name}</h3>
        {s.locality && (
          <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary" data-testid={`alloggi-card-locality-${s.owner_id}`}>
            <MapPin className="h-3 w-3" />{s.locality}
          </span>
        )}
        {s.tagline && <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">{s.tagline}</p>}
        {s.address && <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4 text-primary" />{s.address}</p>}
        <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
          <span className="flex items-center gap-1.5 text-sm text-foreground"><BedDouble className="h-4 w-4 text-primary" />{s.rooms_count} camer{s.rooms_count === 1 ? 'a' : 'e'}</span>
          <span className="flex items-center gap-1 text-sm font-medium text-primary">Scopri <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
        </div>
      </div>
    </Link>
  );
}

export default function Alloggi() {
  const [params, setParams] = useSearchParams();

  const loc = params.get('loc') || '';
  const checkin = params.get('checkin') || '';
  const checkout = params.get('checkout') || '';
  const guests = params.get('guests') || '';
  const cat = params.get('cat') || '';
  const locality = params.get('locality') || '';
  const setCat = (key) => {
    const next = new URLSearchParams(params);
    if (key) next.set('cat', key); else next.delete('cat');
    setParams(next, { replace: true });
  };
  const setLocality = (key) => {
    const next = new URLSearchParams(params);
    if (key) next.set('locality', key); else next.delete('locality');
    setParams(next, { replace: true });
  };

  // Dati via react-query (P2.2): cache condivisa tra pagine + dedup + retry automatico.
  const { data: structures, isError } = useApiQuery(['public', 'hotels'], () => hotelService.publicStructures());
  const items = isError ? [] : (structures ?? null);

  const [ratings, setRatings] = useState({});
  useEffect(() => {
    const ids = (items || []).map((s) => s.owner_id).filter(Boolean);
    if (!ids.length) return;
    cetsSelfService.publicRatings(ids).then((m) => setRatings(m || {})).catch(() => {});
  }, [items]);
  const ov = useLivePageContent(SLUG);

  const counts = useMemo(() => {
    const c = {};
    (items || []).forEach((s) => { const k = s.hospitality_category || 'hotel'; c[k] = (c[k] || 0) + 1; });
    return c;
  }, [items]);

  // Località presenti tra le strutture (per il filtro per zona della Sila).
  const localityOptions = useMemo(() => {
    const set = new Map();
    (items || []).forEach((s) => { if (s.locality) set.set(s.locality, (set.get(s.locality) || 0) + 1); });
    return Array.from(set.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  const filtered = useMemo(() => {
    let list = items || [];
    if (cat) list = list.filter((s) => (s.hospitality_category || 'hotel') === cat);
    if (locality) list = list.filter((s) => (s.locality || '') === locality);
    if (loc) {
      const q = normText(loc);
      list = list.filter((s) => normText([s.name, s.address, s.tagline, s.locality].join(' ')).includes(q));
    }
    return list;
  }, [items, loc, cat, locality]);

  const chips = [];
  if (loc) chips.push({ icon: MapPin, text: loc });
  if (locality) chips.push({ icon: MapPin, text: locality });
  if (checkin && checkout) chips.push({ icon: Calendar, text: `${fmtDate(checkin)} → ${fmtDate(checkout)}` });
  if (guests) chips.push({ icon: Users, text: `${guests} ospit${guests === '1' ? 'e' : 'i'}` });
  const clearFilters = () => setParams({}, { replace: true });

  const heroImage = str(ov.heroImage) || HERO;
  const eyebrow = str(ov.eyebrow) || 'Alloggi';
  const title = str(ov.title) || 'Dove soggiornare in Sila';
  const lead = str(ov.lead) || 'Strutture selezionate, ognuna con la propria vetrina, le camere e i consigli su cosa vedere nei dintorni.';
  const intro = str(ov.intro);

  return (
    <PublicLayout>
      <Seo title="Alloggi — Sila Experiences" description={lead} image={heroImage} />

      {/* HERO */}
      <SilaHero
        image={heroImage}
        badge={eyebrow}
        title={title}
        subtitle={lead}
        icon={Building2}
      />

      {intro && (
        <section className="sila-section pt-12" data-testid="alloggi-intro">
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">{intro}</p>
        </section>
      )}

      {/* MACRO-CATEGORIE (portale Vacanze) */}
      <section className="sila-section pt-10" data-testid="alloggi-categories">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <button
            type="button"
            onClick={() => setCat('')}
            className={`flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-colors ${!cat ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'}`}
            data-testid="alloggi-cat-all"
          >
            <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${!cat ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}><Building2 className="h-5 w-5" /></span>
            <span className="font-display text-sm font-semibold">Tutti</span>
            <span className="text-xs text-muted-foreground">{(items || []).length} strutture</span>
          </button>
          {CATS.map((c) => {
            const on = cat === c.key;
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setCat(c.key)}
                className={`flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-colors ${on ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'}`}
                data-testid={`alloggi-cat-${c.key}`}
              >
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${on ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}><Icon className="h-5 w-5" /></span>
                <span className="font-display text-sm font-semibold">{c.label}</span>
                <span className="text-xs text-muted-foreground">{counts[c.key] || 0} strutture</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* FILTRO LOCALITÀ (zone della Sila) */}
      {localityOptions.length > 1 && (
        <section className="sila-section pt-6" data-testid="alloggi-localities">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 flex items-center gap-1.5 text-sm font-medium text-muted-foreground"><MapPin className="h-4 w-4 text-primary" /> Località:</span>
            <button
              type="button"
              onClick={() => setLocality('')}
              className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${!locality ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border hover:bg-muted'}`}
              data-testid="alloggi-locality-all"
            >
              Tutte
            </button>
            {localityOptions.map(([name, n]) => (
              <button
                key={name}
                type="button"
                onClick={() => setLocality(locality === name ? '' : name)}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${locality === name ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border hover:bg-muted'}`}
                data-testid={`alloggi-locality-${name}`}
              >
                {name} <span className="text-xs text-muted-foreground">({n})</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* LISTA STRUTTURE */}
      <section className="sila-section py-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">Le strutture</h2>
        <p className="mt-1 text-sm text-muted-foreground">Clicca su una struttura per aprirne la vetrina e vedere le camere disponibili.</p>

        <div className="mt-6">
          <ActiveSearchChips chips={chips} onClear={clearFilters} resultCount={filtered.length} noun="strutture" />
          {items === null ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-80 w-full rounded-2xl" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="alloggi-empty">
              <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 font-medium text-foreground">
                {loc ? `Nessuna struttura trovata per “${loc}”` : 'Nessuna struttura disponibile al momento'}
              </p>
              <p className="text-sm text-muted-foreground">
                {loc ? 'Prova a rimuovere i filtri per vedere tutte le strutture.' : 'Le nuove strutture appariranno qui non appena pubblicheranno la loro vetrina.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="alloggi-grid">
              {filtered.map((s) => <StructureCard key={s.owner_id} s={s} rating={ratings[s.owner_id]} />)}
            </div>
          )}
        </div>
      </section>

      {/* Cross-sell: esperienze correlate (spinta all'acquisto) */}
      <ExperiencesCrossSell />
    </PublicLayout>
  );
}
